import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import type { ClientManagementState } from "../types";
import type {
  ClientJobRole,
  ClientUser,
  CentralUser,
  ResetUserPasswordResponse,
  ServiceOverrides,
} from "../api/clientManagement";
import { SectionCard } from "./SectionCard";
import { SettingsSection } from "./SettingsSection";
import { UserEditDialog } from "./UserEditDialog";
import { UserRow } from "./UserRow";
import { buildCsv, downloadCsv } from "../../utils/csv";

interface UserManagementTabProps {
  effectiveClientId: string | null;
  isCentralAdmin: boolean;
  managementState: ClientManagementState;
  setAssignUserId: (value: string) => void;
  assignUserToClientMutation: UseMutationResult<unknown, unknown, string>;
  removeUserFromClientMutation: UseMutationResult<unknown, unknown, string>;
  deleteUserMutation: UseMutationResult<unknown, unknown, string>;
  clientUsersQuery: UseQueryResult<{ data: ClientUser[] }>;
  departmentsList: { id: string; name: string }[];
  jobRolesQuery: UseQueryResult<{ data: ClientJobRole[] }>;
  centralUsersAllQuery: UseQueryResult<{ data: CentralUser[] }>;
  updateUserServicesMutation: UseMutationResult<
    unknown,
    unknown,
    { userId: string; payload: Partial<ServiceOverrides> }
  >;
  resetUserPasswordMutation: UseMutationResult<
    ResetUserPasswordResponse,
    unknown,
    {
      userId: string;
      temporary: boolean;
      sendEmail: boolean;
      password?: string;
      loginUrl?: string;
    }
  >;
  updatingUserServicesId: string | null;
  assignUserToDepartmentMutation: UseMutationResult<
    unknown,
    unknown,
    { departmentId: string; userId: string }
  >;
  removeUserFromDepartmentMutation: UseMutationResult<
    unknown,
    unknown,
    { departmentId: string; userId: string }
  >;
  assignUserToJobRoleMutation: UseMutationResult<
    unknown,
    unknown,
    { jobRoleId: string; userId: string }
  >;
  removeUserFromJobRoleMutation: UseMutationResult<
    unknown,
    unknown,
    { jobRoleId: string; userId: string }
  >;
}

export const UserManagementTab: React.FC<UserManagementTabProps> = ({
  effectiveClientId,
  isCentralAdmin,
  managementState,
  setAssignUserId,
  assignUserToClientMutation,
  removeUserFromClientMutation,
  deleteUserMutation,
  clientUsersQuery,
  departmentsList,
  jobRolesQuery,
  centralUsersAllQuery,
  updateUserServicesMutation,
  resetUserPasswordMutation,
  updatingUserServicesId,
  assignUserToDepartmentMutation,
  removeUserFromDepartmentMutation,
  assignUserToJobRoleMutation,
  removeUserFromJobRoleMutation,
}) => {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortOption>("name-asc");
  const [editingUser, setEditingUser] = useState<ClientUser | null>(null);

  const users = clientUsersQuery.data?.data ?? [];
  const sortedUsers = useSortedUsers(users, search, sort);

  const handleDownloadUsers = () => {
    if (!isCentralAdmin || !sortedUsers.length) return;

    const csv = buildCsv(
      [
        "name",
        "email",
        "departments",
        "job_roles",
        "connected_integrations",
        "wear_jeopardy_status",
        "wear_jeopardy_reason",
        "enabled_services",
        "consent_status",
        "requires_consent",
        "status",
      ],
      sortedUsers.map((user) => {
        const fullName = `${user.first_name} ${user.last_name}`.trim();
        const departments = (user.departments ?? [])
          .map((department) => department.name)
          .filter(Boolean)
          .join("; ");
        const jobRoles = (user.job_roles ?? [])
          .map((role) => role.name)
          .filter(Boolean)
          .join("; ");
        const integrations = (user.integrations ?? [])
          .filter((integration) => integration.connected)
          .map((integration) => integration.name || integration.provider)
          .filter(Boolean)
          .join("; ");
        const enabledServices = Object.entries(user.enabled_services ?? {})
          .filter(([, enabled]) => enabled)
          .map(([serviceKey]) => serviceKey)
          .join("; ");
        const isConnected = !!user.integrations?.some((integration) => integration.connected);

        return [
          fullName,
          user.email,
          departments,
          jobRoles,
          integrations,
          user.wear_jeopardy_status ?? "",
          user.wear_jeopardy_status_reason ?? "",
          enabledServices,
          user.consent_status ?? "",
          user.requires_consent === null || user.requires_consent === undefined
            ? ""
            : user.requires_consent
              ? "Yes"
              : "No",
          isConnected ? "Connected" : "Not connected",
        ];
      }),
    );

    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    downloadCsv(`client-users-${stamp}.csv`, csv);
  };

  const assignableUsers = useMemo(() => {
    if (!centralUsersAllQuery.data?.data) return [];
    if (!effectiveClientId) return [];
    return centralUsersAllQuery.data.data.filter(
      (user) => !user.client?.id || user.client.id === effectiveClientId,
    );
  }, [centralUsersAllQuery.data, effectiveClientId]);

  const selectedAssignableUser = assignableUsers.find(
    (entry) => entry.id === managementState.assignUserId,
  );

  const selectedUserClientName = selectedAssignableUser?.client?.name;
  const isAlreadyAssignedToClient =
    !!selectedAssignableUser?.client?.id &&
    selectedAssignableUser.client.id === effectiveClientId;

  return (
    <SettingsSection
      title="User Management"
      description="User rows are read-only. Click a user row to open the edit modal."
    >
      {!effectiveClientId && (
        <Alert severity="info">Select a client to manage users.</Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <SectionCard
            title="Assign user to client"
            description="Attach an existing user to this client."
          >
            <Stack spacing={2}>
              {isCentralAdmin ? (
                <Autocomplete
                  options={assignableUsers}
                  loading={centralUsersAllQuery.isLoading}
                  value={selectedAssignableUser ?? null}
                  onChange={(_event, value) => setAssignUserId(value?.id ?? "")}
                  getOptionLabel={(option) =>
                    `${option.first_name} ${option.last_name} (${option.email})`
                  }
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      label="User"
                      placeholder="Search users..."
                      helperText={
                        effectiveClientId
                          ? "Only unassigned users or users already on this client can be selected."
                          : "Select a client first to load users."
                      }
                    />
                  )}
                />
              ) : (
                <TextField
                  fullWidth
                  label="User ID"
                  size="small"
                  value={managementState.assignUserId}
                  onChange={(event) => setAssignUserId(event.target.value)}
                />
              )}
              <Button
                variant="contained"
                disabled={
                  !effectiveClientId ||
                  assignUserToClientMutation.isPending ||
                  !managementState.assignUserId.trim() ||
                  isAlreadyAssignedToClient
                }
                onClick={() =>
                  assignUserToClientMutation.mutate(
                    managementState.assignUserId.trim(),
                  )
                }
              >
                Assign to client
              </Button>
              {!!selectedUserClientName && (
                <Typography variant="caption" color="text.secondary">
                  User is already assigned to {selectedUserClientName}.
                </Typography>
              )}
            </Stack>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <SectionCard
            title="Client users"
            description="Read-only list. Open a row to edit the user."
          >
            <Stack spacing={2} sx={{ mb: 2 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search users"
                  placeholder="Name or email"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Sort by</InputLabel>
                  <Select
                    label="Sort by"
                    value={sort}
                    onChange={(event) => setSort(event.target.value as SortOption)}
                  >
                    <MenuItem value="name-asc">Name (A-Z)</MenuItem>
                    <MenuItem value="name-desc">Name (Z-A)</MenuItem>
                    <MenuItem value="email-asc">Email (A-Z)</MenuItem>
                    <MenuItem value="email-desc">Email (Z-A)</MenuItem>
                    <MenuItem value="status-desc">Status (Connected first)</MenuItem>
                    <MenuItem value="status-asc">Status (Disconnected first)</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              {isCentralAdmin && (
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={handleDownloadUsers}
                    disabled={clientUsersQuery.isLoading || sortedUsers.length === 0}
                  >
                    Download filtered list
                  </Button>
                </Stack>
              )}
            </Stack>

            {clientUsersQuery.isLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2">Loading users...</Typography>
              </Stack>
            ) : sortedUsers.length ? (
              <TableContainer sx={{ width: "100%" }}>
                <Table size="small" sx={{ width: "100%" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Departments</TableCell>
                      <TableCell>Job roles</TableCell>
                      <TableCell>Services</TableCell>
                      <TableCell>Wear jeopardy</TableCell>
                      <TableCell>Consent</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedUsers.map((user) => {
                      const isConnected = !!user.integrations?.some(
                        (integration) => integration.connected,
                      );
                      return (
                        <UserRow
                          key={user.id}
                          user={user}
                          statusLabel={isConnected ? "Connected" : "Not connected"}
                          statusTone={isConnected ? "success" : "muted"}
                          onOpenEdit={() => setEditingUser(user)}
                        />
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No users match your search.
              </Typography>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      <UserEditDialog
        open={!!editingUser}
        user={editingUser}
        onClose={() => setEditingUser(null)}
        isCentralAdmin={isCentralAdmin}
        departments={departmentsList}
        jobRoles={jobRolesQuery.data?.data ?? []}
        updateUserServicesMutation={updateUserServicesMutation}
        resetUserPasswordMutation={resetUserPasswordMutation}
        updatingUserServicesId={updatingUserServicesId}
        assignUserToDepartmentMutation={assignUserToDepartmentMutation}
        removeUserFromDepartmentMutation={removeUserFromDepartmentMutation}
        assignUserToJobRoleMutation={assignUserToJobRoleMutation}
        removeUserFromJobRoleMutation={removeUserFromJobRoleMutation}
        removeUserFromClientMutation={removeUserFromClientMutation}
        deleteUserMutation={deleteUserMutation}
      />
    </SettingsSection>
  );
};

type SortOption =
  | "name-asc"
  | "name-desc"
  | "email-asc"
  | "email-desc"
  | "status-asc"
  | "status-desc";

const normalize = (value: string) => value.toLowerCase().trim();

const useSortedUsers = (users: ClientUser[], search: string, sort: SortOption) => {
  return useMemo(() => {
    const filtered = users.filter((user) => {
      if (!search) return true;
      const needle = normalize(search);
      const name = `${user.first_name} ${user.last_name}`.toLowerCase();
      return name.includes(needle) || user.email.toLowerCase().includes(needle);
    });

    const sorted = [...filtered].sort((a, b) => {
      const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
      const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
      const emailA = a.email.toLowerCase();
      const emailB = b.email.toLowerCase();
      const statusA = a.integrations?.some((i) => i.connected) ? 1 : 0;
      const statusB = b.integrations?.some((i) => i.connected) ? 1 : 0;

      switch (sort) {
        case "name-desc":
          return nameB.localeCompare(nameA);
        case "email-asc":
          return emailA.localeCompare(emailB);
        case "email-desc":
          return emailB.localeCompare(emailA);
        case "status-asc":
          return statusA - statusB;
        case "status-desc":
          return statusB - statusA;
        case "name-asc":
        default:
          return nameA.localeCompare(nameB);
      }
    });

    return sorted;
  }, [users, search, sort]);
};
